/**
 * The Pro trial on screen (#2816, spec docs/plans/2026-10-26-free-pro-trial.md,
 * «Показ пробы»). The server decides the trial (`proTrial` of the flag map) and
 * the locks (`gate`, already on the trial's tier); these only say what a
 * screen writes about it.
 */
import type { ProTrialView } from '@aica/api-types/schemas';
import {
  decideTierFeatureAccess,
  findTierFeatureGate,
  type FeatureFlagKey,
} from '@aica/core';
import { PACKAGE_LABEL } from '@/lib/product-package-label';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The plan the trial gives — what «{plan} · trial» names, whatever the
 * feature's own floor: a feature sold from Starter is open on the Pro trial,
 * and there is no Starter trial.
 */
export const PRO_TRIAL_PLAN = PACKAGE_LABEL.pro;

/**
 * The trial opens this feature: Free is below its floor and Pro is not — the
 * trial locks a Free organisation as Pro, by the same rank rule as every
 * lock (`decideTierFeatureAccess`). A feature from Business up stays closed on
 * the trial, and a line promising it would lie.
 */
export function trialOpensFeature(flag: FeatureFlagKey): boolean {
  const gate = findTierFeatureGate(flag);
  if (!gate) return false;
  return (
    !decideTierFeatureAccess('free', gate.minTier, true).allowed &&
    decideTierFeatureAccess('pro', gate.minTier, true).allowed
  );
}

/**
 * Whole days left to `endsAt`, counting a started day as one: the last hours
 * read «1 day left», never «0». An unreadable date reads as 1 — the banner is
 * there because the server says the trial is on.
 */
export function trialDaysLeft(endsAt: string, now: Date): number {
  const left = Math.ceil((Date.parse(endsAt) - now.getTime()) / DAY_MS);
  return Number.isFinite(left) ? Math.max(1, left) : 1;
}

/**
 * Milliseconds until `trialDaysLeft` reads one day less — when the banner
 * redraws on a tab left open. `null` past the end: the re-read at the end
 * (`useTrialRefresh`) takes the banner down.
 */
export function msToNextDayChange(endsAt: string, now: Date): number | null {
  const left = Date.parse(endsAt) - now.getTime();
  if (!Number.isFinite(left) || left <= 0) return null;
  return left % DAY_MS || DAY_MS;
}

/**
 * The tier a screen decides tier-gated items by — the server's `gateTier`
 * for a client that holds the stored tier and the trial apart: Pro while a
 * Free organisation's trial runs, the stored tier otherwise. Free only, as on
 * the server: the two sources are cached apart, and an organisation that has
 * just bought a plan must not read as Pro on a trial answer not yet re-read. The «what's new»
 * pop-up of the web app reads the stored tier from `/users/me`; the embedded
 * one is told this tier by the server, and the two must mark the same items.
 * `proTrial` is non-null only for a Free organisation (`proTrialView`).
 */
export function lockTier<T extends string | null>(
  storedTier: T,
  proTrial: ProTrialView | null
): T | 'pro' {
  return storedTier === 'free' && proTrial?.state === 'active' ? 'pro' : storedTier;
}

/**
 * While the trial waits for its next analysis, how often an open screen asks
 * again: the start is the server's, at a moment the screen cannot know. The web
 * app asks only from a visible tab (`refetchInterval`), the CRM iframe only on
 * focus — a re-read of the embedded session renews it, and only the user's own
 * activity may keep it alive.
 */
export const TRIAL_START_REFRESH_MS = 2 * 60 * 1000;
